import { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, ApiError } from '../api';
import { abbrM, money, nfmt } from '../format';
import { lbl } from '../labels';
import { Button } from '../components/Button';
import { Card, Cards } from '../components/Card';
import { InfoTip } from '../components/InfoTip';
import { useAnchored } from '../components/useAnchored';
import { Select } from '../components/Select';
import { HarvestAndGain, type GainPanel, type HarvestPanel } from './tax/HarvestAndGain';
import { BracketVizView, type BracketViz } from './tax/BracketViz';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { PanePageSize, PanePager } from '../components/PanePageSize';
import type { Cell } from '../components/Table';
import type { ReactNode } from 'react';

/**
 * Tax report — the five summary cards and the quarterly estimated-tax table, from `/api/v1/tax`.
 *
 * This is the most involved page in the app, so it is being ported in slices rather than half-rendered:
 * what is here is complete and measured against the EJS page, and the sections that are not yet
 * (the situation grid, the bracket visualisation, harvest, and the quarterly pay action) are named as open
 * rather than approximated. The cards' tips mirror `partials/tips/tax-from-investments` and `tax-total`.
 */
type Quarterly = {
  quarter: number;
  start: string;
  end: string;
  due: string;
  st: number;
  lt: number;
  div: number;
  suggested: number;
  federal: number;
  state: number;
  /** What the recorded payments leave owed — the status column is computed from these, not from `suggested`. */
  required: number;
  requiredFed: number;
  requiredState: number;
  safeHarbor?: number;
};

type PaidRecord = {
  amount?: number;
  paid_date?: string;
  fed_amount?: number;
  fed_date?: string;
  state_amount?: number;
  state_date?: string;
};

type Est = {
  taxFromInvestments: number;
  total: number;
  effRate: number;
  invEffRate: number;
  stateTax: number;
  niit: number;
  /** Total tax minus the investment delta: the wages/bonus side, assumed covered by withholding. */
  wagesTax?: number;
  divTax?: number;
  prefTiers?: Record<string, number>;
};

/** One row of the year's dividend ledger (`/tax?tab=dividends`). */
type DivRow = { id?: number; tx_date?: string; date?: string; ticker?: string; amount?: number; roc_pct?: number };

const TAB_KEYS = ['summary', 'brackets', 'dividends', 'harvest', 'payments'];

type TaxPayload = {
  /** The reader's card arrangement per tab, from the server (a stored order never hides a card). */
  cardOrder?: Record<string, string[]>;
  year: number;
  years: number[];
  rows: unknown[];
  divs: DivRow[];
  quarterly: {
    rows: Quarterly[];
    paid: Record<string, PaidRecord>;
    totals: { paid?: number; stillDue?: number; through?: number; due?: string; required?: number; short?: number } | null;
    anySplit: boolean;
    harborFactor: number;
    harborMissing: string | null;
    /** The next payment a reader can still make (first due date not yet passed, else the last row). */
    next?: { quarter: number; due: string; suggested: number; federal?: number; state?: number; safeHarbor?: number } | null;
  } | null;
  quarterlyTotals?: { stillDue?: number };
  stTotal: number;
  ltTotal: number;
  targetST: number;
  targetLT: number;
  /** The tax build-up: per-component rows, their totals, and the AGI → taxable-income ladder. */
  taxMatrix: {
    rows?: { comp: string; wagesOnly: number; withInv: number; due: number }[];
    total: { wagesOnly: number; withInv: number; due: number };
    agi: number;
    std: number;
    taxable: { ordinaryWithInv: number; preferential: number };
    lossDeducted?: number;
    lossCarryforward?: number;
  } | null;
  /** The page's own sentence about how this year's gains are taxed (`gainCtx` on the EJS page). */
  gainCtx?: string;
  divTaxable: number;
  est: Est;
  marg: { ordRate: number; ltRate: number; niit: number; stateRate: number; nextLongTermRate?: number };
  profile: { state?: string; est_income?: number; est_bonus?: number; filing_status?: string };
  harvest: HarvestPanel | null;
  gain: GainPanel | null;
  bracketViz: BracketViz;
  /** Dividends received before return of capital, and the ROC share itself. */
  divTotal?: number;
  divROC?: number;
  /** Short-term + long-term realized, added server-side so the card shows a number, not a sum. */
  realizedTotal?: number;
};

/** Days from today to a due date, as views/tax.ejs counts them (negative once it has passed). */
function daysLeft(due: string): number {
  const ms = new Date(due + 'T00:00:00').getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
}
/** The EJS page's words under a due date: "in 12 days", "due today". */
function daysLine(due: string): string {
  const n = daysLeft(due);
  return n === 0 ? 'due today' : 'in ' + n + ' day' + (n === 1 ? '' : 's');
}

/** The filing-status vocabulary, verbatim from views/tax.ejs. */
const FILING_LABELS: Record<string, string> = {
  single: 'Single',
  married: 'Married jointly',
  mfs: 'Married separately',
  hoh: 'Head of household',
  qss: 'Qualifying survivor',
};

/** A section heading with a `...` menu holding one CSV download — the EJS page's `.csv-menu-btn` shape. */
function ExportMenu({
  trigger,
  heading,
  href,
  columns,
  rows,
  empty,
  footer,
}: {
  trigger: string;
  heading: string;
  href: string;
  /** Rendered inside the card, under the table: a pager belongs to the table it pages, not to the page. */
  footer?: React.ReactNode;
  /** The page's own sentence for a year with nothing in it — views/tax.ejs prints it instead of a table. */
  empty: string;
  columns: { label: React.ReactNode; num?: boolean }[];
  rows: { key: string; cells: Cell[] }[];
}) {
  const [open, setOpen] = useState(false);
  // Same promotion the tax page's payment popover needed, and the EJS page gets from its layout script: an open
  // [data-menu] is drawn as a fixed overlay from its trigger's rect, not as an absolutely positioned box that the
  // document can clip (or that a scroll container can grow around).
  const csvBtn = useRef<HTMLButtonElement | null>(null);
  const csvPop = useRef<HTMLDivElement | null>(null);
  const at = useAnchored(open, csvBtn, csvPop, { fallbackWidth: 160, fallbackHeight: 40, onClose: () => setOpen(false) });
  return (
    <section className="panel-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>{heading}</h2>
        <span style={{ position: 'relative' }}>
          <button
            ref={csvBtn}
            type="button"
            className="ghost csv-menu-btn"
            data-csv={href}
            data-menu-trigger={trigger}
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{ fontWeight: 700 }}
          >
            …
          </button>
          <div
            ref={csvPop}
            id={trigger}
            className="csv-menu"
            style={{
              display: open ? 'block' : 'none',
              position: at ? 'fixed' : 'absolute',
              ...(at ? { top: at.top, left: at.left } : { right: 0, top: '100%' }),
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              minWidth: 160,
              zIndex: 30,
            }}
            data-menu
          >
            {/* The link is the only choice this menu has: it downloads and leaves, so the menu closes with it
                (the EJS page gets the same from the layout's shared binder). */}
            <a href={href} className="tax-body" onClick={() => setOpen(false)} style={{ display: 'block', padding: '8px 13px', textDecoration: 'none' }}>
              Download CSV
            </a>
          </div>
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="muted tax-body">{empty}</p>
      ) : (
        // views/tax.ejs passes no tableId for these two tables, so they carry no id at all — an id here
        // would be one the reference never had.
        <Table expandable={false} wrap tableClass="" columns={columns} rows={rows} />
      )}
      {footer}
    </section>
  );
}

export function Tax() {
  const [year] = useState<number | null>(null);
  // The tab IS the URL (openspec: tax-reports). A link, a reload and the back button all land on the same
  // view, and the year selector carries the current tab with it rather than resetting to Summary.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') || '';
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'summary';
  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next);
  };
  // The dividends ledger's pager: 25 by default, which is roughly a quarter's worth of income.
  const [divSize, setDivSize] = useState('25');
  const [divPage, setDivPage] = useState(1);
  // The profile is behind its own Edit control, as on the EJS page — the situation grid is the input the whole
  // page's estimate is computed from, and it should not be permanently open in the reader's way.
  const [editing, setEditing] = useState(false);
  /** A save that failed, shown under the form instead of closing it as if it had worked. */
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<{ est_income: string; est_bonus: string; state: string; filing_status: string } | null>(null);
  const qc = useQueryClient();
  const q = useQuery<TaxPayload, ApiError>({
    queryKey: ['tax', year],
    queryFn: () => apiGet<TaxPayload>(year ? `/api/v1/tax?year=${year}` : '/api/v1/tax'),
  });

  // The reader's card arrangement, seeded from the payload. It is state, not derived: a drag has to render
  // immediately and the POST that stores it must not be in the render path. Declared ABOVE the loading guard,
  // with every other hook (a hook after a return is React #310).
  const [orders, setOrders] = useState<Record<string, string[]>>(() => (q.data?.cardOrder as Record<string, string[]>) || {});
  const [dragKey, setDragKey] = useState<string | null>(null);
  const dragRef = useRef<{
    which: string;
    key: string;
    x: number;
    y: number;
    /** Where the pointer is now, and where inside the card it grabbed — the card is placed at pointer - grab. */
    px: number;
    py: number;
    grabX: number;
    grabY: number;
    active: boolean;
    pointerId: number | null;
    el?: HTMLElement;
    /** The order the gesture started from: what a cancelled drag reverts to, and what "did it move?" compares. */
    startOrder: string[];
    moved: boolean;
  } | null>(null);
  /** The row being dragged in — its cards are measured to decide the slot the dragged card takes, live. */
  const rowRef = useRef<HTMLDivElement | null>(null);
  /** Card positions from before the latest reorder, for the FLIP pass that animates cards into their new slots. */
  const flipRects = useRef<Map<string, DOMRect> | null>(null);
  /**
   * The touch gesture's handlers, re-pointed at the current render's closures.
   *
   * Touch listens through NATIVE listeners on the row, not React props: React attaches touchmove passively, where
   * preventDefault() is ignored — and iPad Safari needs that preventDefault to stop taking the gesture back (it
   * starts a scroll on the first diagonal pixel and fires pointercancel, which is why pointer events alone made
   * the drag feel dead on an iPad).
   */
  const touchApi = useRef<{ start: (e: TouchEvent) => void; move: (e: TouchEvent) => void; end: (e: TouchEvent) => void } | null>(null);
  /** The newest order, for the pointerup that has to persist it (state updates are async). */
  const ordersRef = useRef<Record<string, string[]>>(orders);
  useEffect(() => { ordersRef.current = orders; }, [orders]);
  // A pointer cancelled by the browser (a swipe that turns into a scroll) must not leave the body locked.
  useEffect(() => () => { document.body.classList.remove('card-dragging'); }, []);
  // The first payload is the seed; a year change brings that year's own arrangement with it.
  useEffect(() => { if (q.data?.cardOrder) setOrders(q.data.cardOrder); }, [q.data?.cardOrder]);

  /**
   * The live reorder's motion: cards that moved to a new slot animate FROM their old position (FLIP).
   *
   * The reorder itself is a state change — the row is correct the instant React commits — but without this the
   * cards teleport and the eye reads it as a flicker rather than a slide. Runs on every render and does nothing
   * unless a drag step recorded positions.
   */
  const cardEls = (root: HTMLElement) =>
    Array.from(root.querySelectorAll<HTMLElement>('.cards > .card[data-card-key]'));

  /**
   * Put the dragged card under the pointer.
   *
   * The slot logic alone moves the card only when it crosses a boundary, so between boundaries it sat still —
   * the row previewed the ORDER but the card did not follow the hand. This translates it by
   * (pointer - grab point) from wherever its slot currently is, and it is re-run after every reorder commit:
   * the slot changed underneath, so the offset has to be recomputed or the card would jump a cell.
   */
  const followPointer = () => {
    const st = dragRef.current;
    if (!st || !st.active || !st.el) return;
    const el = st.el;
    // Measure untransformed: getBoundingClientRect reports the current transform, which would feed back in.
    el.style.transform = 'none';
    const r = el.getBoundingClientRect();
    const dx = (st.px - st.grabX) - r.left;
    const dy = (st.py - st.grabY) - r.top;
    el.style.transform = `translate(${dx}px, ${dy}px) scale(1.02)`;
  };

  useLayoutEffect(() => {
    const before = flipRects.current;
    if (!before) return;
    flipRects.current = null;
    const row = rowRef.current;
    if (!row) return;
    cardEls(row).forEach((el) => {
      // The dragged card is under the pointer, not in its slot: its transform belongs to followPointer().
      if (el.classList.contains('is-dragging')) return;
      const b = before.get(el.getAttribute('data-card-key') as string);
      if (!b) return;
      const a = el.getBoundingClientRect();
      const dx = b.left - a.left;
      const dy = b.top - a.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0px, 0px)' }],
        { duration: 160, easing: 'cubic-bezier(.2,.7,.3,1)' },
      );
    });
    // A reorder just committed, so the dragged card's slot moved: re-anchor it to the pointer.
    followPointer();
  });

  // Native touch listeners: bound when the row exists (after the page loads, and again for another tab), removed
  // with it. `passive: false` on touchmove is the point — see touchApi above.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const start = (e: TouchEvent) => touchApi.current?.start(e);
    const move = (e: TouchEvent) => touchApi.current?.move(e);
    const end = (e: TouchEvent) => touchApi.current?.end(e);
    row.addEventListener('touchstart', start, { passive: true });
    row.addEventListener('touchmove', move, { passive: false });
    row.addEventListener('touchend', end);
    row.addEventListener('touchcancel', end);
    return () => {
      row.removeEventListener('touchstart', start);
      row.removeEventListener('touchmove', move);
      row.removeEventListener('touchend', end);
      row.removeEventListener('touchcancel', end);
    };
  }, [q.data, tab]);


  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load the tax report: {q.error.message}</main>;

  const d = q.data!;
  const { stTotal, ltTotal, divTaxable, est, marg } = d;
  // The safe-harbor figure the EJS page states under the quarterly table: the same number on every quarter,
  // so the first row's is the one to quote.
  const shVal = d.quarterly?.rows?.[0]?.safeHarbor ?? null;
  // Safe harbor against this year's estimate: the note states it, and the card shows the multiple, only when the
  // two actually differ (a year whose estimate is above the harbor has nothing to warn about).
  const safeHarborRatio = shVal && est.total > 0 ? shVal * 4 / est.total : null;
  const safeHarborOverpay = shVal ? Math.max(0, shVal * 4 - est.total) : 0;
  // The dividend ledger: paged, then grouped by quarter with a subtotal that is the sum of the rows it
  // actually covers (a page can hold part of a quarter, and a subtotal that claimed the whole quarter would
  // be a lie on that page). Openspec: tax-reports, "The dividend ledger is paged and grouped by quarter".
  const divAll = d.divs;
  const divPageSize = divSize === 'all' ? Math.max(1, divAll.length) : Number(divSize) || 25;
  const divPages = Math.max(1, Math.ceil(divAll.length / divPageSize));
  const divPageNow = Math.min(Math.max(1, divPage), divPages);
  const divSlice = divAll.slice((divPageNow - 1) * divPageSize, divPageNow * divPageSize);
  // The view's own date idiom: "Apr 15", not an ISO string — a table of quarters reads as a calendar.


  // ---- the tab's five cards ---------------------------------------------------------------------------
  // Five on every tab (openspec: tax-reports, "The headline cards answer the page's question"). Positions carry
  // no meaning across tabs — every card is self-labelled — and the COUNT never changes, so the row's height is
  // constant and the panels below do not jump when a tab is switched.
  const fig = (v: number | null | undefined) => (v == null || !Number.isFinite(v)
    ? '—'
    : <span title={money(v)}>{abbrM(v)}</span>);
  const pct = (r: number | null | undefined) => (r == null || !Number.isFinite(r) ? '—' : `${(r * 100).toFixed(2)}%`);
  const tip = (content: React.ReactNode) => <InfoTip anchor="card" icon="tax" width={280}>{content}</InfoTip>;
  // The tax cards' one colour rule (openspec: tax-reports, "The tax cards use one colour rule"): income received
  // this year is green, a loss red, and a cost or a zero neutral — a green $0.00 states a result that did not
  // happen, and a coloured tax figure would read as a loss.
  const sign = (n: number | null | undefined) => (n == null || !Number.isFinite(n) || n === 0 ? '' : n > 0 ? 'pos' : 'neg');
  const qt = d.quarterly;
  // Still due is the one card whose COLOUR is the answer: red while the year is short, green once the payments
  // overshoot (a credit, not a cost), neutral when nothing is due — and the sub-line says which of the three it
  // is rather than always reading "short $0.00".
  const stillDue = qt?.totals?.stillDue ?? null;
  const duePaid = qt?.totals?.paid ?? 0;
  // Red while the four quarters still ask for money, green once nothing is left to pay AND something was paid
  // (a credit, not a cost), and the plain ink when the year asks nothing at all. The rows cap each quarter at
  // zero, so "overpaid" shows as a stillDue of 0 with money already recorded.
  const stillDueClass = stillDue == null || stillDue === 0 ? (stillDue === 0 && duePaid > 0 ? 'pos' : '') : 'neg';
  const stillDueNote = qt?.totals
    ? stillDue != null && stillDue > 0
      ? `short ${money(qt.totals.short ?? 0)} through Q${qt.totals.through ?? 0}`
      : duePaid > 0
        ? `all four quarters covered · ${money(duePaid)} paid`
        : 'nothing due this year'
    : '';

  // Each card is a FACTORY keyed by the page's own card key: the same visual card appears on several tabs with a
  // different key, so each tab's arrangement is independent and a stored order can never hide a card
  // (openspec: tax-reports, "The reader can arrange the cards").
  const cardState = (k: string) => (dragKey === k ? 'is-dragging' : undefined);

  const cardTotal = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Total tax')} value={fig(est.total)} note={<span className="muted tax-small">{pct(est.effRate)} effective rate</span>}
      tip={tip(<>All estimated taxes combined: federal ordinary income (wages + bonus), capital gains, dividends,
        NIIT and state. The percentage is the effective rate: total tax ÷ AGI.</>)} />
  );
  const cardInv = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('From investments')} value={fig(est.taxFromInvestments)}
      note={<span className="muted tax-small">{pct(est.invEffRate)} effective rate</span>}
      tip={tip(<>Federal tax on short- and long-term gains, the dividends tax, NIIT and the state tax the gains
        themselves add. &ldquo;From wages&rdquo; is the rest of the total, assumed covered by payroll withholding.</>)} />
  );
  const cardDue = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Still due')} style={{ borderColor: 'var(--accent)' }}
      valueClass={stillDueClass} value={fig(stillDue)}
      note={<span className="muted tax-small">{stillDueNote}</span>}
      tip={tip(<>What the four quarterly payments still ask for this year — a later loss can leave earlier quarters
        overpaid, and the excess comes back at filing. It counts toward the investment figure, not the total tax.</>)} />
  );
  const cardRealized = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Realized gains')} valueClass={sign(d.realizedTotal)} value={fig(d.realizedTotal)}
      note={<span className="muted tax-small">{money(ltTotal)} long · {stTotal ? money(stTotal) : '$0'} short</span>}
      tip={tip(<>Long- and short-term gains realized this year. The next long-term dollar costs {pct(marg.ltRate)}
        federal + {pct(marg.niit)} NIIT + {pct(marg.stateRate)} {d.profile?.state || 'state'} = {pct(marg.nextLongTermRate)}.</>)} />
  );
  const cardDivs = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Dividends received')} valueClass={divTaxable > 0 ? 'pos' : ''} value={fig(d.divTotal)}
      note={<span className="muted tax-small">{money(divTaxable)} taxable after ROC</span>}
      tip={tip(<>All dividends recorded this year. Only the taxable part is taxed now; the rest is return of capital,
        which reduces cost basis instead.</>)} />
  );
  const cardPaid = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Paid to date')} value={fig(qt?.totals?.paid)}
      note={<span className="muted tax-small">of {money(est.taxFromInvestments)} the quarters cover</span>}
      tip={tip(<>Payments recorded for this year, against the investment delta the four quarters cover — not the total
        tax, whose wages side is assumed withheld.</>)} />
  );
  const cardNext = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Next payment')} value={fig(qt?.next?.suggested)}
      note={<span className="muted tax-small">{qt?.next ? <>Q{qt.next.quarter} due {qt.next.due} · fed {money(qt.next.federal ?? 0)} + state {money(qt.next.state ?? 0)}</> : '—'}</span>}
      tip={tip(<>The next due date and that quarter&rsquo;s estimate, federal and state shown separately.</>)} />
  );
  const cardHarbor = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Safe harbor')} value={fig(shVal)}
      note={<span className="muted tax-small">{safeHarborRatio ? `4 payments = ${safeHarborRatio.toFixed(2)}× the estimate` : 'per quarter'}</span>}
      tip={tip(<>A quarter of 100%/110% of last year&rsquo;s total tax: paying it guarantees no underpayment penalty
        whatever this year&rsquo;s gains do{safeHarborRatio && safeHarborRatio > 1.2 ? `, but here four of them are ${safeHarborRatio.toFixed(2)}× this year's estimate — paying in full would overpay` : ''}.</>)} />
  );
  const cardOrd = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Ordinary taxable')} value={fig(d.taxMatrix?.taxable?.ordinaryWithInv)}
      note={<span className="muted tax-small">top bracket {pct(marg.ordRate)}</span>}
      tip={tip(<>Taxable ordinary income: AGI minus the standard deduction, with gains taxed at ordinary rates and the
        loss deduction applied.</>)} />
  );
  const cardPref = (k: string) => {
    const tiers = est.prefTiers || {};
    const prefNote = Object.entries(tiers).filter(([, v]) => Number(v) > 0).map(([r, v]) => `${r}% on ${money(Number(v))}`).join(' · ') || 'none occupied';
    return (
      <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Preferential')} value={fig(d.taxMatrix?.taxable?.preferential)}
        note={<span className="muted tax-small">{prefNote}</span>}
        tip={tip(<>Long-term gains plus taxable dividends. These fill the 0/15/20% tiers on top of ordinary income.</>)} />
    );
  };
  const cardOrdNext = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Next ordinary dollar')} value={pct(marg.ordRate)}
      note={<span className="muted tax-small">{d.bracketViz?.federal?.headroom ? `${money(d.bracketViz.federal.headroom.amount)} of room before the ${pct(d.bracketViz.federal.headroom.rate)} bracket` : 'top bracket, no further headroom'}</span>}
      tip={tip(<>The bracket the next ordinary dollar lands in, and how much room is left in the current one before the
        rate steps up.</>)} />
  );
  const cardLtNext = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Next long-term dollar')} value={pct(marg.nextLongTermRate)}
      note={<span className="muted tax-small">{pct(marg.ltRate)} fed + {pct(marg.niit)} NIIT + {pct(marg.stateRate)} {d.profile?.state || 'state'}</span>}
      tip={tip(<>The rate the next dollar of long-term gain pays: the long-term rate, NIIT and the state rate.</>)} />
  );
  const cardLoss = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Loss available')} value={fig(d.harvest ? Math.abs(d.harvest.totalLoss || 0) : null)}
      note={<span className="muted tax-small">{d.harvest?.rows?.length ?? 0} open lots</span>}
      tip={tip(<>Open lots trading below cost: the loss you could realize. Wash-sale disallowed and deferred amounts
        are priced by the planner, not subtracted here.</>)} />
  );
  const cardRoom = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Room at 0%')} value={fig(d.gain?.headroom?.room)}
      note={<span className="muted tax-small">{(d.gain?.headroom?.room ?? 0) > 0 ? 'before the next tier' : 'none left this year'}</span>}
      tip={tip(<>How much long-term gain can still be realized at the 0% rate this year. When ordinary income has
        already filled the 0% tier this is zero, and the ladder below shows the room left in the next one.</>)} />
  );
  const cardSave = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Value of a harvested dollar')} value={pct(est.invEffRate)}
      note={<span className="muted tax-small">each $1 of loss saves ~{pct(est.invEffRate)}</span>}
      tip={tip(<>A realized loss offsets a dollar of gain, saving the effective rate on investments until the
        year&rsquo;s gains are zero; beyond that it offsets up to $3,000 of ordinary income and carries forward.</>)} />
  );
  const cardTaxable = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Taxable after ROC')} valueClass={divTaxable > 0 ? 'pos' : ''} value={fig(divTaxable)}
      note={<span className="muted tax-small">{d.divTotal ? `${pct(divTaxable / d.divTotal)} of the total` : '—'}</span>}
      tip={tip(<>Received minus the return-of-capital share — the figure the year&rsquo;s dividend tax is computed on.</>)} />
  );
  const cardRoc = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Return of capital')} value={fig(d.divROC)}
      note={<span className="muted tax-small">{d.divTotal ? `${pct((d.divROC ?? 0) / d.divTotal)} · reduces basis` : '—'}</span>}
      tip={tip(<>The non-taxable share: it reduces cost basis instead, raising a later gain. The ROC % of each payment
        is editable on this tab.</>)} />
  );
  const cardDivTax = (k: string) => (
    <Card key={k} cardKey={k} className={cardState(k)} label={lbl('Dividend tax')} value={fig(est.divTax)}
      note={<span className="muted tax-small">part of the {money(est.taxFromInvestments)} investment tax</span>}
      tip={tip(<>Federal and state tax on the taxable dividend share, inside the investment figure.</>)} />
  );

  /** The card set for a tab, keyed by the page's card keys (the server validates stored orders against them). */
  const cardSet = (which: string): Record<string, () => React.ReactNode> => {
    if (which === 'payments') return { 'due-p': () => cardDue('due-p'), paid: () => cardPaid('paid'), next: () => cardNext('next'), harbor: () => cardHarbor('harbor'), 'total-p': () => cardTotal('total-p') };
    if (which === 'brackets') return { ord: () => cardOrd('ord'), pref: () => cardPref('pref'), ordn: () => cardOrdNext('ordn'), ltn: () => cardLtNext('ltn'), 'total-b': () => cardTotal('total-b') };
    if (which === 'harvest') return { 'realized-h': () => cardRealized('realized-h'), loss: () => cardLoss('loss'), room: () => cardRoom('room'), save: () => cardSave('save'), 'inv-h': () => cardInv('inv-h') };
    if (which === 'dividends') return { 'divs-d': () => cardDivs('divs-d'), taxable: () => cardTaxable('taxable'), roc: () => cardRoc('roc'), dtax: () => cardDivTax('dtax'), 'inv-d': () => cardInv('inv-d') };
    // Key order IS the default order (the server's TAX_CARD_KEYS.summary lists the same sequence).
    return { realized: () => cardRealized('realized'), divs: () => cardDivs('divs'), total: () => cardTotal('total'), inv: () => cardInv('inv'), due: () => cardDue('due') };
  };

  const orderFor = (which: string) => {
    const set = cardSet(which);
    const keys = Object.keys(set);
    const stored = orders[which] || [];
    return [...stored.filter((k) => keys.includes(k)), ...keys.filter((k) => !stored.includes(k))];
  };

  const persistOrder = (which: string, order: string[]) => {
    setOrders((o) => ({ ...o, [which]: order }));
    fetch('/api/v1/prefs/card-order', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tab: which, order }),
    }).catch(() => {});
  };

  const move = (which: string, from: string, to: string, before: boolean) => {
    const order = orderFor(which).filter((k) => k !== from);
    const at = order.indexOf(to);
    order.splice(before ? at : at + 1, 0, from);
    persistOrder(which, order);
  };

  const cardsRow = (which: string) => {
    const set = cardSet(which);

    /** Finish the gesture. `commit` false is a cancellation: the row goes back to where it started. */
    const endDrag = (commit: boolean) => {
      const st = dragRef.current;
      document.body.classList.remove('card-dragging');
      // Settle: a card released mid-air slides back into the slot the row is showing, rather than vanishing
      // from under the cursor. Cleared inline transform + an animation from the release position to none.
      if (st && st.el) {
        const el = st.el;
        const from = el.style.transform;
        el.style.transform = '';
        if (from) el.animate([{ transform: from }, { transform: 'none' }], { duration: 140, easing: 'cubic-bezier(.2,.7,.3,1)' });
      }
      if (st && st.active) {
        if (!commit) {
          ordersRef.current = { ...ordersRef.current, [which]: st.startOrder };
          setOrders((o) => ({ ...o, [which]: st.startOrder }));
        } else if (st.moved) {
          const order = ordersRef.current[which];
          if (order) persistOrder(which, order);
        }
      }
      dragRef.current = null;
      setDragKey(null);
    };

    /**
     * Which slot the dragged card takes, as an index among the cards that are NOT being dragged.
     *
     * The nearest card's centre wins, so this holds up when the row wraps to two lines; then the direction
     * decides placement — x inside a card's band (before or after its midpoint), y when the pointer is in the
     * gap between lines and no card is beside it.
     */
    const slotFor = (key: string, x: number, y: number) => {
      const row = rowRef.current;
      if (!row) return 0;
      const others = cardEls(row).filter((el) => el.getAttribute('data-card-key') !== key);
      if (!others.length) return 0;
      // Same-row cards win outright. A wrapped row puts the second line's cards ~120px away vertically, which
      // beats the 240px to the next card on the pointer's own line — and the drag then reordered into the
      // WRONG line on a 10px move.
      const inRow = others
        .map((el, i) => ({ el, i, r: el.getBoundingClientRect() }))
        .filter(({ r }) => y >= r.top - 8 && y <= r.bottom + 8);
      const pool = inRow.length ? inRow : others.map((el, i) => ({ el, i, r: el.getBoundingClientRect() }));
      let bestIdx = pool[0].i;
      let bestRect = pool[0].r;
      let bestDist = Infinity;
      pool.forEach(({ i, r }) => {
        const dist = Math.abs(x - (r.left + r.width / 2));
        if (dist < bestDist) { bestDist = dist; bestRect = r; bestIdx = i; }
      });
      const inBand = y >= bestRect.top - 8 && y <= bestRect.bottom + 8;
      const after = inBand ? x > bestRect.left + bestRect.width / 2 : y > bestRect.top + bestRect.height / 2;
      return bestIdx + (after ? 1 : 0);
    };

    // The row reorders AS the card is dragged: the pointer decides the slot, the array is rewritten on the spot,
    // and the FLIP pass above slides the cards there. Nothing persists until the pointer is released.
    //
    // The order this reads is the REF's, not the state's: a pointermove can arrive before React has committed
    // the previous step, and applying a DOM-measured slot index to a stale array is what made the card jump two
    // slots on a 12px move and then oscillate back.
    const orderNow = () => {
      const keys = Object.keys(cardSet(which));
      const stored = ordersRef.current[which] || [];
      return [...stored.filter((k) => keys.includes(k)), ...keys.filter((k) => !stored.includes(k))];
    };
    const liveTo = (key: string, x: number, y: number) => {
      const at = slotFor(key, x, y);
      const order = orderNow();
      if (order.indexOf(key) === at) return;
      const row = rowRef.current;
      if (row) {
        // Measure BEFORE the commit. Finish in-flight animations first: getBoundingClientRect reports the
        // animated position, which would double-count and make the next step jump.
        cardEls(row).forEach((el) => el.getAnimations().forEach((a) => a.finish()));
        const rects = new Map<string, DOMRect>();
        cardEls(row).forEach((el) => rects.set(el.getAttribute('data-card-key') as string, el.getBoundingClientRect()));
        flipRects.current = rects;
      }
      const next = order.filter((k) => k !== key);
      next.splice(at, 0, key);
      ordersRef.current = { ...ordersRef.current, [which]: next };
      setOrders((o) => ({ ...o, [which]: next }));
      const st = dragRef.current;
      if (st) st.moved = true;
    };

    /** Track a gesture from a card, whichever input produced it (pointerId null = touch). */
    const begin = (el: HTMLElement, x: number, y: number, pointerId: number | null) => {
      const r = el.getBoundingClientRect();
      dragRef.current = {
        which,
        key: el.getAttribute('data-card-key') as string,
        x,
        y,
        px: x,
        py: y,
        grabX: x - r.left,
        grabY: y - r.top,
        active: false,
        pointerId,
        el,
        startOrder: orderFor(which),
        moved: false,
      };
    };

    const activate = (st: NonNullable<typeof dragRef.current>) => {
      st.active = true;
      try { if (st.pointerId != null) (st.el as HTMLElement).setPointerCapture(st.pointerId); } catch (err) { /* released */ }
      document.body.classList.add('card-dragging');
      setDragKey(st.key);
    };

    const moveTo = (st: NonNullable<typeof dragRef.current>, x: number, y: number) => {
      st.px = x;
      st.py = y;
      followPointer();
      liveTo(st.key, x, y);
    };

    // Touch drives the same gesture through touch events (see touchApi): iPad Safari cancels pointer events as soon
    // as the finger drifts toward the vertical, so a pointer-only drag only ever worked on a perfectly straight
    // horizontal line. A mostly-vertical swipe is still the page's: past 8px it abandons and lets it scroll.
    touchApi.current = {
      start: (e) => {
        if (e.touches.length !== 1) { dragRef.current = null; return; }
        const t = e.touches[0];
        const target = e.target as HTMLElement;
        const cardEl = target.closest('[data-card-key]') as HTMLElement | null;
        if (!cardEl || target.closest('a,button,input,textarea,select,.info-tip')) return;
        begin(cardEl, t.clientX, t.clientY, null);
      },
      move: (e) => {
        const st = dragRef.current;
        if (!st || st.pointerId != null) return;   // a mouse/pen gesture owns this one
        const t = e.touches[0];
        if (!t) return;
        if (!st.active) {
          const dx = t.clientX - st.x;
          const dy = t.clientY - st.y;
          if (Math.hypot(dx, dy) < 8) return;
          if (Math.abs(dy) >= Math.abs(dx)) { dragRef.current = null; return; }
          activate(st);
        }
        // Ours now: keep the page from scrolling and Safari from taking the gesture back mid-drag.
        if (e.cancelable) e.preventDefault();
        moveTo(st, t.clientX, t.clientY);
      },
      end: () => {
        const st = dragRef.current;
        if (!st || st.pointerId != null) return;
        endDrag(!!st.active);
      },
    };

    return (
      <div
        ref={rowRef}
        style={{ display: 'contents' }}
        onPointerDown={(e) => {
          // Touch is handled by the touch listeners above: Safari's pointer stream is not dependable for drags.
          if (e.pointerType === 'touch') return;
          const target = e.target as HTMLElement;
          const cardEl = target.closest('[data-card-key]') as HTMLElement | null;
          // A card is dragged from anywhere ON it — the whole card is the handle. Links, buttons, fields and the
          // ⓘ keep their own behaviour, and only the primary mouse button starts a reorder.
          if (!cardEl || target.closest('a,button,input,textarea,select,.info-tip')) return;
          if (e.button !== 0) return;
          begin(cardEl, e.clientX, e.clientY, e.pointerId);
        }}
        onPointerMove={(e) => {
          const st = dragRef.current;
          if (!st || st.pointerId == null) return;
          if (!st.active) {
            if (Math.hypot(e.clientX - st.x, e.clientY - st.y) < 6) return;
            activate(st);
          }
          // The card follows the hand; the row reorders under it. Both, every move.
          moveTo(st, e.clientX, e.clientY);
        }}
        // Only this gesture's own end: a touch drag's pointercancel must not end it (its pointerId is null).
        onPointerUp={() => { if (dragRef.current && dragRef.current.pointerId != null) endDrag(true); }}
        onPointerCancel={() => { if (dragRef.current && dragRef.current.pointerId != null) endDrag(false); }}
        onKeyDown={(e) => {
          // Arrow keys move a focused card: Alt+Arrow and plain Arrow both, because a browser with a hardware
          // keyboard attached does not always deliver the Alt with the arrow, and on a card the plain arrows do
          // nothing else.
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          const target = e.target as HTMLElement;
          if (target.closest('a,button,input,textarea,select')) return;
          const el = target.closest('[data-card-key]');
          if (!el) return;
          const k = el.getAttribute('data-card-key') as string;
          const order = orderFor(which);
          const i = order.indexOf(k);
          const j = e.key === 'ArrowLeft' ? i - 1 : i + 1;
          if (i < 0 || j < 0 || j >= order.length) return;
          e.preventDefault();
          move(which, k, order[j], e.key === 'ArrowLeft');
          requestAnimationFrame(() => {
            (document.querySelector(`[data-card-key="${k}"]`) as HTMLElement | null)?.focus();
          });
        }}
      >
        <Cards>
          {orderFor(which).map((k) =>
            cloneElement(set[k]() as React.ReactElement<{ tabIndex?: number }>, { tabIndex: 0 }),
          )}
        </Cards>
      </div>
    );
  };

  return (
    <main>
      <div id="tax-wrap">
      {/* views/tax.ejs opens with the title, the year picker and the "taxable accounts only" note on one row.
          The SPA had the title and the note but no picker, so a past year could not be reached from the page. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <h1 style={{ margin: 0, position: 'relative', top: -2 }}>Tax report</h1>
        <Select
          id="tax-year"
          name="year"
          className="tax-year"
          value={String(d.year)}
          fontSize={22}
          fontWeight={700}
          height={30}
          ariaLabel="Tax year"
          options={d.years.filter((y) => y > 2000).map((y) => ({ value: String(y), label: String(y) }))}
          onChange={(v) => { if (v) window.location.href = '/tax?year=' + v + '&tab=' + tab; }}
        />
        <span className="muted tax-small" style={{ marginLeft: 'auto' }}>
          Taxable accounts only · {d.year} brackets · Estimate, not tax advice
        </span>
      </div>
      {/* The report's four facets, as tabs under the year and its cards (openspec: tax-reports). The page was
          5,773px of one column; the year picker and the cards stay above the bar because every tab reports on
          that year and a bracket ladder with no total is just a diagram. */}
      <Tabs
        id="tax-tabs"
        ariaLabel="Tax report sections"
        // Segmented, like every other bar in the app (Dividends, Equity): a track with a raised thumb.
        variant="segmented"
        raised
        // The gap under a tab bar is the app's one value: 16px, as the settings page has always had.
        style={{ margin: '4px 0 16px' }}
        // Plain strings, NOT lbl(): a tab is not a card or a column header, and the full/short pair renders
        // BOTH outside those ("Dividendsdiv" was the short form leaking next to the long one).
        // Order (openspec: tax-reports): evidence next to the summary it qualifies, then the two actions in the
        // order a year happens — harvest in December, the January payment after it.
        items={[
          { key: 'summary', label: 'Summary', pane: 'tax-tab-summary' },
          { key: 'brackets', label: 'Brackets', pane: 'tax-tab-brackets' },
          { key: 'dividends', label: 'Dividends', pane: 'tax-tab-dividends' },
          { key: 'harvest', label: 'Harvest', pane: 'tax-tab-harvest' },
          { key: 'payments', label: 'Payments', pane: 'tax-tab-payments' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* The tab's own five cards, under the bar: they summarise what the panels below them detail, so they
          belong to the tab, not to the page. Five on every tab — a constant count means the row never reflows. */}
      {cardsRow(tab)}
      {tab === 'summary' && (
        <div id="tax-tab-summary">
      {/* The situation card, as views/tax.ejs draws it: one row of Filing/State/Wages/Bonus with the Edit
          control pinned top-right, swapped for the profile form while editing, and gainCtx under it. The SPA
          rendered a single muted line, so the four figures the page is built on were not on the page. */}
      <section className="panel-card" style={{ position: 'relative' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Your {d.year} situation
          <InfoTip anchor="inline" icon="tax" width={280}>
            Assumptions used for every estimate on this page. Edit to change wages, bonus, state, or filing
            status.
          </InfoTip>
        </h2>
        {editing && form ? (
          <form
            id="tax-profile-form"
            action="/tax/profile"
            method="post"
            style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', margin: '10px 0 14px' }}
            onSubmit={async (e) => {
              e.preventDefault();
              setSaveError('');
              const res = await fetch('/api/v1/tax/profile', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                // `filing` is the field name the page's form posts and the service reads; the state here is
                // `filing_status`, which is the name it is READ back under. Sending only the latter stored 'single'
                // on every save.
                body: JSON.stringify({
                  year: d.year,
                  est_income: form.est_income,
                  est_bonus: form.est_bonus,
                  state: form.state,
                  filing: form.filing_status,
                }),
              });
              if (!res.ok) {
                // A failed save used to close the form as if it had worked.
                const body = await res.json().catch(() => ({}));
                setSaveError(body.error || `Could not save (HTTP ${res.status}).`);
                return;
              }
              setEditing(false);
              qc.invalidateQueries({ queryKey: ['tax'] });
            }}
          >
            <input type="hidden" name="year" value={d.year} />
            {/* The form reads in the card's order — Filing / State / Wages / Bonus — so editing a line does not
                move every field. Rules first (how you file, where), then the amounts they apply to. */}
            <label className="tax-small" style={{ display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--muted)' }}>
              Filing status
              <select name="filing" style={{ width: 200 }} value={form.filing_status}
                onChange={(e) => setForm({ ...form, filing_status: e.target.value })}>
                <option value="single">Single</option>
                <option value="married">Married filing jointly</option>
                <option value="mfs">Married filing separately</option>
                <option value="hoh">Head of household</option>
                <option value="qss">Qualifying surviving spouse</option>
              </select>
            </label>
            <label className="tax-small" style={{ display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--muted)' }}>
              State
              <input name="state" maxLength={2} placeholder="CA" style={{ width: 60, textTransform: 'uppercase' }}
                value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
            </label>
            <label className="tax-small" style={{ display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--muted)' }}>
              Estimated Wages
              <input name="est_income" type="number" min={0} step={1} placeholder="e.g. 120000" style={{ width: 160 }}
                value={form.est_income} onChange={(e) => setForm({ ...form, est_income: e.target.value })} />
            </label>
            <label className="tax-small" style={{ display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--muted)' }}>
              Estimated Bonus
              <input name="est_bonus" type="number" min={0} step={1} placeholder="e.g. 20000" style={{ width: 160 }}
                value={form.est_bonus} onChange={(e) => setForm({ ...form, est_bonus: e.target.value })} />
            </label>
            <button type="button" className="ghost btn-confirm-x" id="tax-profile-cancel" title="Cancel"
              style={{ marginBottom: 4 }} onClick={() => setEditing(false)}>✕</button>
            <button type="submit" className="ghost btn-confirm-ok" title="Save" style={{ marginBottom: 4 }}>✓</button>
            {saveError ? (
              <div className="error t-small" style={{ flexBasis: '100%', margin: '4px 0 0' }}>{saveError}</div>
            ) : null}
          </form>
        ) : (
          <div className="tax-body" id="situation-card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span>
              Filing <strong>{FILING_LABELS[String(d.profile?.filing_status || 'single')] || String(d.profile?.filing_status || '')}</strong>
            </span>
            <span className="muted">·</span>
            <span>
              State <strong>{d.profile?.state || '—'}</strong>
            </span>
            <span className="muted">·</span>
            <span>
              Wages <strong>${nfmt(d.profile?.est_income ?? 0)}</strong>
            </span>
            <span className="muted">·</span>
            <span>
              Bonus <strong>${nfmt(d.profile?.est_bonus ?? 0)}</strong>
            </span>
            <button
              type="button"
              id="situation-edit"
              className="ghost"
              style={{ position: 'absolute', top: 20, right: 22 }}
              onClick={() => {
                setForm({
                  est_income: String(d.profile?.est_income ?? ''),
                  est_bonus: String(d.profile?.est_bonus ?? ''),
                  state: d.profile?.state || '',
                  filing_status: d.profile?.filing_status || 'single',
                });
                setEditing(true);
              }}
            >
              Edit
            </button>
          </div>
        )}
        <p className="muted tax-small" style={{ margin: '8px 0 0' }}>{d.gainCtx}</p>
      </section>

      {/* The tax matrix: each component of the bill wages-only, with investments, and the difference. The EJS
          page's own words for its empty state, which is what a reader with no wages set sees. */}
      <section className="panel-card">
        <h2>Where the tax comes from</h2>
        {!d.taxMatrix ? (
          <p className="muted tax-body">Set your estimated wages and bonus above to see the breakdown.</p>
        ) : (
          <Table
            expandable={false}
            tableId="tax-matrix"
            tableClass=""
            columns={[
              { label: lbl('Component') },
              { label: lbl('Wages only'), num: true },
              { label: lbl('With investments'), num: true },
              { label: lbl('Due to investments'), num: true },
            ]}
            rows={[
              ...(d.taxMatrix.rows || []).map((r, i) => ({
                key: String(i),
                cells: [
                  r.comp,
                  r.wagesOnly ? money(r.wagesOnly) : '—',
                  r.withInv ? money(r.withInv) : '—',
                  r.due ? money(r.due) : '—',
                ],
              })),
              {
                key: 'total',
                rowAttrs: { style: 'border-top: 2px solid var(--border);' },
                cells: [
                  <strong key="c">Total</strong>,
                  <strong key="w">{money(d.taxMatrix.total.wagesOnly)}</strong>,
                  <strong key="i">{money(d.taxMatrix.total.withInv)}</strong>,
                  <strong key="d">{money(d.taxMatrix.total.due)}</strong>,
                ],
              },
            ]}
          />
        )}
      </section>


        </div>
      )}

      {tab === 'brackets' && (
        <div id="tax-tab-brackets">
      {/* The build-up from AGI to taxable income, headerless as in views/tax.ejs — it is a sum a reader reads
          top to bottom, not a table of records. */}
      {d.taxMatrix ? (
        <section className="panel-card">
          <h2>Income breakdown</h2>
          <Table
            expandable={false}
            header={false}
            tableId="income-buildup"
            tableClass=""
            columns={[{ label: '' }, { label: '', num: true }]}
            rows={[
              { key: 'agi', cells: ['AGI (wages + bonus + investments)', '$' + nfmt(d.taxMatrix.agi)] },
              { key: 'std', cells: ['Standard deduction', '−$' + nfmt(d.taxMatrix.std)] },
              { key: 'ord', cells: ['Taxable income — ordinary', '$' + nfmt(d.taxMatrix.taxable.ordinaryWithInv)] },
              { key: 'pref', cells: ['Preferential income (LT + dividends)', '$' + nfmt(d.taxMatrix.taxable.preferential)] },
              ...((d.taxMatrix.lossDeducted ?? 0) > 0
                ? [{ key: 'ld', cells: ['Capital loss deducted from ordinary income', '−$' + nfmt(d.taxMatrix.lossDeducted)] }]
                : []),
              ...((d.taxMatrix.lossCarryforward ?? 0) > 0
                ? [{ key: 'lc', cells: ['Capital loss carried forward to ' + (d.year + 1), '$' + nfmt(d.taxMatrix.lossCarryforward)] }]
                : []),
            ]}
          />
        </section>
      ) : null}

          {/* views/bracket_viz.ejs draws its own heading; the SPA's section carried an invented one. */}
          <section className="panel-card">
            <h2>Bracket breakdown</h2>
            <BracketVizView viz={d.bracketViz} />
          </section>
        </div>
      )}

      {tab === 'harvest' && (
        <div id="tax-tab-harvest">
      {/* The two exports live behind their own ... menus, one per section, as on the EJS page: a CSV button on
          the page would invite the reader to wonder which table it exports. Each menu holds the link, and the
          link is a real file URL — the browser has to receive a named download. */}
      <ExportMenu
        trigger="csv-menu-gains"
        heading="Realized gains"
        href={`/tax/gains.csv?year=${d.year}`}
        empty={`No disposals in ${d.year}.`}
        // views/tax.ejs's realized-gains table, column for column: the per-share figures, the wash-sale
        // verdict and the estimated federal/state tax each disposal creates. The SPA showed eight columns,
        // which dropped the two per-share columns, the wash-sale column and both estimates.
        columns={[
          { label: lbl('Sold') },
          { label: lbl('Ticker') },
          { label: lbl('Qty'), num: true },
          { label: lbl('Acquired') },
          { label: lbl('Proceeds'), num: true },
          { label: lbl('Sale/share'), num: true },
          { label: lbl('Cost Basis'), num: true },
          { label: lbl('Cost/share'), num: true },
          { label: lbl('Gain/Loss'), num: true },
          { label: lbl('Wash sale') },
          { label: lbl('Term') },
          { label: lbl('Fed Est'), num: true },
          { label: (d.profile?.state || 'State') + ' Est (' + (d.marg.stateRate * 100).toFixed(2) + '%)', num: true },
        ]}
        rows={(d.rows as Record<string, unknown>[]).map((r, i) => {
          const wt = (r.washText as { headline?: string; detail?: string; tier?: string } | undefined) || {
            headline: Number(r.realized_gain ?? 0) < 0 ? 'No' : '',
            detail: '',
            tier: 'none',
          };
          const bad = wt.tier === 'disallowed' || wt.tier === 'mixed';
          const gain = Number(r.realized_gain ?? 0);
          const long = r.term === 'long';
          // The estimates exist only when the profile has wages: without them there is no bracket to price
          // the gain in, and the EJS page prints a dash rather than a guess.
          let fed: string | ReactNode = '—';
          let st: string | ReactNode = '—';
          if (d.profile?.est_income != null && d.profile.est_income !== ('' as unknown as number)) {
            const rate = (long ? d.marg.ltRate : d.marg.ordRate) + d.marg.niit;
            const f = gain >= 0 ? gain * rate : 0;
            fed = f ? `${money(f)} (${(rate * 100).toFixed(2)}% ${long ? 'LT' : 'ST'})` : '—';
            const stAmt = gain >= 0 ? gain * d.marg.stateRate : 0;
            st = stAmt ? money(stAmt) : '—';
          }
          const wash = wt.headline ? (
            <>
              <span className={bad ? 'neg' : ''}>{wt.headline}</span>
              {wt.detail ? (
                <span className="muted tax-tiny" style={{ display: 'block', ...(bad ? { color: 'var(--red)' } : {}) }}>
                  {wt.detail}
                </span>
              ) : null}
            </>
          ) : (
            <span className="muted">—</span>
          );
          return {
            key: String(r.id ?? i),
            cells: [
              String(r.disposed_date ?? r.sold_date ?? r.date ?? '—'),
              <strong key="t">{String(r.ticker ?? '—')}</strong>,
              nfmt(Number(r.quantity ?? 0)),
              String(r.acquired_date ?? '—'),
              money(Number(r.proceeds ?? 0)),
              money(Number(r.proceeds_per_share ?? 0)),
              money(Number(r.basis ?? 0)),
              money(Number(r.cost_per_share ?? 0)),
              <span key="g" className={gain >= 0 ? 'pos' : 'neg'}>{money(gain)}</span>,
              wash,
              <span key="tt" className="tag">{long ? 'Long' : 'Short'}</span>,
              <span key="f" className="muted">{fed}</span>,
              <span key="s" className="muted">{st}</span>,
            ],
          };
        })}
      />

      <HarvestAndGain
        harvest={d.harvest}
        gain={d.gain}
        year={d.year}
        stTotal={stTotal}
        ltTotal={ltTotal}
        targetSt={d.targetST}
        targetLt={d.targetLT}
        marg={d.marg}
        lossDeducted={d.taxMatrix?.lossDeducted ?? 0}
        lossCarryforward={d.taxMatrix ? d.taxMatrix.lossCarryforward ?? 0 : null}
      />
        </div>
      )}

      {tab === 'dividends' && (
        <div id="tax-tab-dividends">
      <ExportMenu
        trigger="csv-menu-divs"
        heading="Dividends"
        href={`/tax/dividends.csv?year=${d.year}`}
        empty={`No dividends in ${d.year}.`}
        columns={[
          { label: lbl('Date') },
          { label: lbl('Ticker') },
          { label: lbl('Amount'), num: true },
          { label: lbl('ROC %'), num: true },
          { label: lbl('Taxable Amount'), num: true },
        ]}
        rows={divSlice.map((r, i) => {
          const amt = Number(r.amount ?? 0);
          const roc = Number(r.roc_pct ?? 0);
          return {
            key: String(r.id ?? i),
            rowAttrs: { 'data-div-id': String(r.id ?? ''), 'data-amount': String(amt) },
            cells: [
              String(r.tx_date ?? r.date ?? '—'),
              String(r.ticker ?? '—'),
              money(amt),
              // Click to edit, in place: the ROC is one number on one dividend, and a modal for it would be a
              // page away from the figure it changes.
              { content: <RocCell txid={Number(r.id)} roc={roc} amount={amt} /> },
              <span key="t" className="taxable-amt">{money(amt * (1 - roc / 100))}</span>,
            ] as Cell[],
          };
        })}
        footer={
          <div className="pane-pagebar" style={{ marginTop: 8, marginBottom: 0, justifyContent: 'space-between' }}>
            <span className="muted tax-small">
              {divAll.length} payment{divAll.length === 1 ? '' : 's'} in {d.year} · projection and monthly/yearly
              breakdowns on <Link to="/dividends">Dividends</Link>
            </span>
            {divAll.length > divPageSize && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <PanePageSize value={divSize} onChange={(v) => { setDivSize(v); setDivPage(1); }} noun="payments" total={divAll.length} id="tax-div-size" />
                <PanePager
                  total={divAll.length}
                  size={divPageSize}
                  page={divPageNow}
                  onPage={setDivPage}
                  noun="payments"
                  all={divSize === 'all'}
                  id="tax-div-pager"
                />
              </span>
            )}
          </div>
        }
      />
        </div>
      )}

      {tab === 'payments' && (
        <div id="tax-tab-payments">
      <section className="panel-card">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Quarterly estimated taxes
          <InfoTip anchor="inline" icon="tax" width={300}>
            Two ways to size each payment. <strong>Estimated Tax</strong> (pay as you go) re-estimates tax on the
            gains actually realized in that quarter (wages spread evenly) — the amount is the combined federal +
            state estimate, with the two parts shown beneath it. <strong>Safe harbor</strong> pays ¼ of
            100%/110% of last year&apos;s total tax — guarantees no underpayment penalty regardless of this
            year&apos;s gains.
          </InfoTip>
        </h2>
        {!d.quarterly || d.quarterly.rows.length === 0 ? (
          <p className="muted tax-body">Set your estimated wages/bonus in the profile below to compute quarterly payments.</p>
        ) : (
          <>
            <Table
              expandable={false}
              tableId="quarterly-table"
              tableClass=""
              alignRows
              columns={[
                { label: lbl('Quarter') },
                { label: lbl('Due Date') },
                {
                  // The column does not equal the long-term card, and the tip says why: it counts short-term
                  // gains, long-term gains AND taxable dividends realized in that quarter.
                  label: (
                    <>
                      {lbl('Realized in quarter')}{' '}
                      {/* `textAlign: 'left'` is the page's own rule (views/tax.ejs writes it on every tip it draws):
                          the header cell is `num`, so the bubble inherits right-alignment and the prose reads as a
                          ragged block against the right edge. */}
                      <InfoTip anchor="inline" place="above-center" icon="tiny" width={290} contentStyle={{ textAlign: 'left' }}>
                        This column is the quarter&apos;s <strong>short-term gains</strong> +{' '}
                        <strong>long-term gains</strong> + <strong>taxable dividends</strong> (after any return of
                        capital). The four quarters therefore add up to those three cards above, not to the
                        long-term card on its own — dividends are in here because the quarter&apos;s estimated tax
                        is computed on them.
                      </InfoTip>
                    </>
                  ),
                  num: true,
                },
                { label: lbl('Estimated Tax'), num: true },
                { label: lbl('Paid') },
                { label: lbl('Status') },
              ]}
              rows={d.quarterly.rows.map((r) => {
                const paid = d.quarterly!.paid[r.quarter] || {};
                // The EJS view's own rule: "short" is about whether money is still owed, not about whether
                // a payment exists; a part payment is underpaid, a missing one is not due yet or owed.
                const hasParts = paid.fed_amount != null || paid.state_amount != null;
                const hasRecord = (paid.amount || 0) > 0 || (paid.fed_amount || 0) > 0 || (paid.state_amount || 0) > 0;
                const short = hasParts ? r.requiredFed > 0 || r.requiredState > 0 : r.required > 0;
                // "required" while the quarter is not due yet, "short" once it is: the same word the EJS page
                // uses, because "short" before the due date reads as a mistake the reader should already fix.
                const dueDay = new Date(r.due + 'T00:00:00');
                const notDueYet = dueDay >= new Date();
                const status = short ? (notDueYet ? 'not due' : 'underpaid') : hasRecord ? 'paid' : 'met';
                const owedWord = notDueYet ? 'required' : 'short';
                // views/tax.ejs splits the shortfall two ways: per destination while the record has parts (or no
                // record at all), and a single "Total short" line when only an unsplit total was recorded — a
                // per-destination figure in that case would invent a split the reader never entered.
                const perDest = !hasRecord || hasParts;
                const showParts = !!d.quarterly!.anySplit && perDest && (r.requiredFed > 0 || r.requiredState > 0);
                const showTotal = hasRecord && !perDest && r.required > 0;
                return {
                  key: r.quarter,
                  rowAttrs: { 'data-q': r.quarter },
                  cells: [
                    <>
                      Q{r.quarter}{' '}
                      <span className="muted tax-tiny" style={{ display: 'block' }}>
                        {md(r.start)} – {md(r.end)}
                      </span>
                    </>,
                    <>
                      {md(r.due)}
                      {daysLeft(r.due) >= 0 ? (
                        <span
                          className={`${daysLeft(r.due) <= 14 ? 'neg' : 'muted'} tax-tiny`}
                          style={{ display: 'block' }}
                        >
                          {daysLine(r.due)}
                        </span>
                      ) : null}
                    </>,
                    // views/tax.ejs colours the realized figure: a gain prints in the positive ink, a loss in the
                    // negative one, and a flat quarter stays plain.
                    (() => {
                      const realized = r.st + r.lt + r.div;
                      return realized > 0 ? (
                        <span className="pos">{money(realized)}</span>
                      ) : realized < 0 ? (
                        <span className="neg">{money(realized)}</span>
                      ) : (
                        money(realized)
                      );
                    })(),
                    <>
                      {money(r.suggested)}
                      {r.suggested > 0 ? (
                        <span className="muted tax-tiny" style={{ display: 'block' }}>
                          Fed {money(r.federal)}
                          {r.state ? ` · ${d.profile?.state || 'state'} ${money(r.state)}` : ''}
                        </span>
                      ) : null}
                    </>,
                    {
                      // The cell holds a control, and a control is not aligned by vertical-align: middle the
                      // way text is — measured, it sat 7px below the row's first line. Top-aligned like its
                      // neighbours, the button's box starts on that line.
                      content: (
                        <PaidCell
                          q={r}
                          paid={paid}
                          stateName={d.profile?.state || 'State'}
                          anySplit={!!d.quarterly!.anySplit}
                          year={d.year}
                        />
                      ),
                      className: 'q-pay',
                      attrs: { style: 'min-width: 150px; vertical-align: top;' },
                    },
                    {
                      // views/tax.ejs colours the WORD (span.pos / span.neg / span.muted) and leaves the cell
                      // bare: the cell's class list is part of the table's contract.
                      className: 'q-status',
                      content: (
                        <>
                      <span className={status === 'underpaid' ? 'neg' : status === 'paid' ? 'pos' : 'muted'}>
                        {status === 'paid' ? 'Paid' : status === 'met' ? 'No tax' : status === 'underpaid' ? 'Underpaid' : 'Not due'}
                      </span>
                      {showParts && r.requiredFed > 0 ? (
                        <span className="muted tax-tiny" style={{ display: 'block', whiteSpace: 'nowrap' }}>
                          Fed {owedWord} {money(r.requiredFed)}
                        </span>
                      ) : null}
                      {showParts && r.requiredState > 0 ? (
                        <span className="muted tax-tiny" style={{ display: 'block', whiteSpace: 'nowrap' }}>
                          {d.profile?.state || 'state'} {owedWord} {money(r.requiredState)}
                        </span>
                      ) : null}
                      {showTotal ? (
                        <span className="muted tax-tiny" style={{ display: 'block', whiteSpace: 'nowrap' }}>
                          Total {owedWord} {money(r.required)}
                        </span>
                      ) : null}
                        </>
                      ),
                    },
                  ],
                };
              })}
            />
            {/* views/tax.ejs states the safe harbor in one of three sentences, then the running paid-to-date
                line, then an empty #qp-hint the menu script writes into. The SPA had replaced all three with an
                effective-rate sentence the EJS page does not say. */}
            {shVal == null ? (
              <p className="muted tax-small" style={{ margin: '8px 0 0' }}>
                {d.quarterly.harborMissing === 'zero'
                  ? `Safe harbor: ${d.year - 1}'s estimated total tax was $0.00, so there is no safe-harbor figure to state — each quarter stands on its own estimate.`
                  : `Safe harbor: add a ${d.year - 1} profile to compute it — it is at most ¼ of last year's estimated total tax.`}
              </p>
            ) : (
              <p className="muted tax-small" style={{ margin: '8px 0 0' }}>
                Safe harbor: at most <strong>{money(shVal)}</strong> a quarter — ¼ of{' '}
                {d.quarterly.harborFactor > 1 ? '110% of ' : ''}
                {`${d.year - 1}'s estimated total tax`}
                {d.quarterly.harborFactor > 1 ? ' (110% is the rule for income of $150,000 or more).' : '.'} No
                quarter requires more than this, and it is the same for every quarter.
              </p>
            )}
            {d.quarterly.totals?.through ? (
              <p className="muted tax-small" style={{ margin: '6px 0 0' }}>
                Paid to date: <strong>{money(d.quarterly.totals.paid ?? 0)}</strong> of{' '}
                {money(d.quarterly.totals.required ?? 0)} required by {md(d.quarterly.totals.due ?? '')}
                {(d.quarterly.totals.short ?? 0) > 0 ? (
                  <>
                    {' — '}
                    <span className="neg">{money(d.quarterly.totals.short ?? 0)} short</span>
                  </>
                ) : (
                  <>
                    {' — '}
                    <span className="pos">nothing outstanding</span>
                  </>
                )}
                . This line moves as payments land; the rows above are each judged on their own quarter.
              </p>
            ) : null}
            <p className="muted tax-small" id="qp-hint" />
          </>
        )}
        {/* The sentence the page never said (openspec: tax-reports, "The quarterly payments say what they
            cover"): the four payments cover the INVESTMENT delta, not the total tax, and safe harbor can be
            several times this year's estimate. Both figures are computed; neither is written here. */}
        {d.quarterly?.totals ? (
          <p className="muted tax-body" id="quarterly-note" style={{ marginTop: 12 }}>
            The four payments above cover the tax on your investments ({money(est.taxFromInvestments)}); the tax on
            wages and bonus ({money(est.wagesTax ?? 0)}) is assumed covered by payroll withholding — which is why{' '}
            <strong>Still due</strong> counts toward the investment figure, not the total tax.
            {safeHarborRatio && safeHarborRatio > 1.2 ? (
              <>
                {' '}Paying all four guarantees no penalty and would overpay the year by{' '}
                {money(safeHarborOverpay)}.
              </>
            ) : null}
          </p>
        ) : null}
      </section>
        </div>
      )}
      </div>
    </main>
  );
}

/**
 * The Paid cell: a summary button over a popover, because the split needs six fields and they would cost
 * ~190px of column width inline. Saving posts to `/tax/quarterly` and reloads — the shortfall it produces is a
 * server figure, and a browser that recomputed tax would be a second implementation of it.
 */
function PaidCell({
  q,
  paid,
  stateName,
  anySplit,
  year,
}: {
  q: Quarterly;
  paid: PaidRecord;
  stateName: string;
  anySplit: boolean;
  year: number;
}) {
  const [open, setOpen] = useState(false);
  const [fed, setFed] = useState(paid.fed_amount == null ? '' : String(paid.fed_amount));
  const [state, setState] = useState(paid.state_amount == null ? '' : String(paid.state_amount));
  // views/tax.ejs fills the Total field only when the record has no split (`!hasParts && paid.amount`): a quarter
  // paid as Fed + State shows two figures in the rows above and an EMPTY total, not their sum. Pre-filling it with
  // the sum put 702.1 in a field the page leaves at 0.00.
  const [total, setTotal] = useState(
    paid.fed_amount == null && paid.state_amount == null && paid.amount ? String(paid.amount) : '',
  );
  const [fedDate, setFedDate] = useState<Segs>(segsOf(paid.fed_date));
  const [stateDate, setStateDate] = useState<Segs>(segsOf(paid.state_date));
  const [totalDate, setTotalDate] = useState<Segs>(segsOf(paid.paid_date));
  // The popover hangs in an absolutely positioned box, and the quarterly table lives in a scroll container —
  // so an open popover taller than the row made that container scroll and pushed the panel down. It is placed as
  // a fixed overlay from the button's own rect instead (the EJS page can use `top:46` because its table is not
  // scroll-wrapped), clamped to the viewport and flipped above the button when there is no room below.
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  // Anchored and re-anchored on scroll, exactly as views/layout.ejs's PFOverlay does it.
  const at = useAnchored(open, btnRef, popRef, { onClose: () => setOpen(false) });

  const hasParts = paid.fed_amount != null || paid.state_amount != null;
  const hasRecord = (paid.amount || 0) > 0 || (paid.fed_amount || 0) > 0 || (paid.state_amount || 0) > 0;
  const perDest = !hasRecord || hasParts;
  const fedShown = (paid.fed_amount || 0) > 0;
  const stateShown = (paid.state_amount || 0) > 0;
  const filled = (hasParts && (fedShown || stateShown)) || (paid.amount || 0) > 0;

  const save = async () => {
    const body: Record<string, unknown> = { year, quarter: q.quarter };
    const num = (s2: string) => (s2.trim() === '' ? null : parseFloat(s2) || 0);
    if (num(fed) != null || num(state) != null) {
      body.fed_amount = num(fed);
      body.fed_date = segDate(fedDate);
      body.state_amount = num(state);
      body.state_date = segDate(stateDate);
      body.paid_date = null;
    } else {
      body.amount = num(total) || 0;
      body.paid_date = segDate(totalDate);
      body.fed_amount = null;
      body.fed_date = null;
      body.state_amount = null;
      body.state_date = null;
    }
    const res = await fetch('/tax/quarterly', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) window.location.reload();
  };

  const line = (label: string, amt: number, day?: string) =>
    `${label} ${money(amt)}${day ? ` · ${md(day)}` : amt > 0 ? ' · no date' : ''}`;

  return (
    // The wrapper is relative so the popover can hang off the button; the <td> itself stays a table cell.
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        className={'ghost qp-open ' + (filled ? 'is-filled' : 'is-empty')}
        data-q={q.quarter}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {hasParts && (fedShown || stateShown) ? (
          <>
            {fedShown ? (
              <span className="tax-tiny" style={{ display: 'block' }}>{line('Fed', paid.fed_amount || 0, paid.fed_date)}</span>
            ) : null}
            {stateShown ? (
              <span className="tax-tiny" style={{ display: 'block' }}>
                {line(stateName, paid.state_amount || 0, paid.state_date)}
              </span>
            ) : null}
          </>
        ) : (paid.amount || 0) > 0 ? (
          <>
            <span className="tax-tiny" style={{ display: 'block' }}>{line('Total', paid.amount || 0, paid.paid_date)}</span>
            <span className="muted tax-tiny" style={{ display: 'block' }}>split not recorded</span>
          </>
        ) : (
          <span className="muted tax-tiny" style={{ display: 'block' }}>+ Add payment</span>
        )}
      </button>

      {/* The popover is ALWAYS in the DOM, hidden until its trigger opens it — as views/tax.ejs renders it.
          Mounting it on open made the cell's text read as "+ Add payment" alone (the reference's cell carries
          the popover's labels too), and it re-mounted the inputs' state on every close. */}
      <div
        ref={popRef}
        id={`qp-pay-${q.quarter}`}
        // `data-menu` is the shared popover contract (the EJS page's markup carries it and the outside-click
        // handler keys on it); without it the popover was invisible to anything that looks for a menu, and it
        // had no shadow — the thing that makes it read as floating over the row rather than sitting in it.
        data-menu
        style={{
          display: open ? 'block' : 'none',
          position: at ? 'fixed' : 'absolute',
          ...(at ? { top: at.top, left: at.left } : { right: 0, top: 46 }),
          zIndex: 30,
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 10,
          minWidth: 300,
          textAlign: 'left',
          boxShadow: '0 8px 30px var(--scrim)',
        }}
      >
          <div className="tax-small" style={{ marginBottom: 6 }}>
            Owed this quarter: Fed {money(Math.max(0, q.federal))} · {stateName} {money(Math.max(0, q.state))}
          </div>
          {/* views/tax.ejs says the same two things: a quarter whose activity reduced the tax owes nothing, and a
              record that holds only a total keeps a total shortfall — it is never split into destinations the
              reader did not enter. */}
          {q.federal < 0 || q.state < 0 ? (
            <div className="muted tax-small" style={{ marginBottom: 6 }}>
              This quarter&apos;s realized activity reduced the tax, so nothing is owed for it — the amount below
              is what was paid, if anything.
            </div>
          ) : null}
          {anySplit && perDest && (q.requiredFed > 0 || q.requiredState > 0) ? (
            <div className="muted tax-small" style={{ marginBottom: 6 }}>
              Still short:{' '}
              {q.requiredFed > 0 ? `Fed ${money(q.requiredFed)}` : ''}
              {q.requiredFed > 0 && q.requiredState > 0 ? ' · ' : ''}
              {q.requiredState > 0 ? `${stateName} ${money(q.requiredState)}` : ''}
            </div>
          ) : hasRecord && !perDest && q.required > 0 ? (
            <div className="muted tax-small" style={{ marginBottom: 6 }}>
              Still short: {money(q.required)} <span className="muted">(a total is not split between the two)</span>
            </div>
          ) : null}
          <div className="muted tax-small" style={{ marginBottom: 8 }}>Each destination is paid on its own day</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span className="tax-small" style={{ width: 40 }}>Fed</span>
            <DateSegs cls="qp-fed-date" quarter={q.quarter} segs={fedDate} onChange={setFedDate} />
            <input type="number" step={0.01} min={0} className="qp-fed" style={QP_AMOUNT} placeholder="0.00" value={fed} onChange={(e) => setFed(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span className="tax-small" style={{ width: 40 }}>{stateName}</span>
            <DateSegs cls="qp-state-date" quarter={q.quarter} segs={stateDate} onChange={setStateDate} />
            <input type="number" step={0.01} min={0} className="qp-state" style={QP_AMOUNT} placeholder="0.00" value={state} onChange={(e) => setState(e.target.value)} />
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginBottom: 10 }}>
            <div className="muted tax-small" style={{ marginBottom: 6 }}>Or a total, if the split is not known</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="tax-small" style={{ width: 40 }}>Total</span>
              <DateSegs cls="qp-total-date" quarter={q.quarter} segs={totalDate} onChange={setTotalDate} />
              <input
                type="number"
                step={0.01}
                min={0}
                className="qp-total"
                style={QP_AMOUNT}
                placeholder="0.00"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
              />
            </div>
          </div>
          <Button className="qp-pay-save btn-sm" onClick={() => void save()}>
            Save
          </Button>
        </div>
    </div>
  );
}

type Segs = { mm: string; dd: string; yy: string };

/** 'YYYY-MM-DD' as the three boxes the popover edits them in. */
function segsOf(date: string | undefined | null): Segs {
  if (!date) return { mm: '', dd: '', yy: '' };
  return { mm: date.slice(5, 7), dd: date.slice(8, 10), yy: date.slice(0, 4) };
}

/** The three boxes as a date, or null when any of them is empty — a partial date is no date. */
function segDate(s: Segs): string | null {
  if (!s.mm || !s.dd || !s.yy) return null;
  return `${s.yy}-${s.mm}-${s.dd}`;
}

/** MM / DD / YYYY, three inputs in one editable date. */
/** The three-segment date the page draws, with the page's own geometry: `flex: 1 1 auto` so it takes the
 *  width the row has left, a 34px box, and borderless 26/26/40px segments inside it. Without the flex the control
 *  kept its intrinsic 116px and the amount field stopped 32px short of the popover's right edge. */
// 14px is written here rather than inherited: the page's segments take the input base size, and a stale
// stylesheet carrying an old `.qp-date input { font-size: 11px }` was enough to make them render small on a page
// that had otherwise loaded the new bundle.
const QP_SEG: React.CSSProperties = {
  border: 'none',
  padding: 0,
  textAlign: 'center',
  background: 'transparent',
  fontSize: 14,
  height: 16,
};

function DateSegs({
  cls,
  segs,
  quarter,
  onChange,
}: {
  cls: string;
  segs: Segs;
  /** The popover row's quarter, which the page writes on the box as `data-q` alongside the ISO `data-date`. */
  quarter?: number;
  onChange: (s: Segs) => void;
}) {
  const iso = segs.yy && segs.mm && segs.dd ? segs.yy + '-' + segs.mm + '-' + segs.dd : '';
  return (
    <span
      className={'qp-date ' + cls}
      data-q={quarter}
      data-date={iso}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flex: '1 1 auto',
        minWidth: 0,
        height: 34,
        minHeight: 34,
        maxHeight: 34,
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '0 6px',
      }}
    >
      <input className="qp-seg qp-mm" style={{ ...QP_SEG, width: 26 }} maxLength={2} inputMode="numeric" placeholder="MM" value={segs.mm} onChange={(e) => onChange({ ...segs, mm: e.target.value })} />
      <span className="muted">/</span>
      <input className="qp-seg qp-dd" style={{ ...QP_SEG, width: 26 }} maxLength={2} inputMode="numeric" placeholder="DD" value={segs.dd} onChange={(e) => onChange({ ...segs, dd: e.target.value })} />
      <span className="muted">/</span>
      <input className="qp-seg qp-yy" style={{ ...QP_SEG, width: 40 }} maxLength={4} inputMode="numeric" placeholder="YYYY" value={segs.yy} onChange={(e) => onChange({ ...segs, yy: e.target.value })} />
    </span>
  );
}

/** The page's amount field next to a date: a fixed 92px, right-aligned, 34px tall, 0.00 when empty. */
const QP_AMOUNT: React.CSSProperties = {
  width: 92,
  height: 34,
  minHeight: 34,
  maxHeight: 34,
  lineHeight: '32px',
  padding: '0 10px',
  textAlign: 'right',
  fontSize: 14,
};

/** The view's own date idiom: "Apr 15", not an ISO string — a table of quarters reads as a calendar. */
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function md(d8: string): string {
  return `${MON[Number(d8.slice(5, 7)) - 1]} ${Number(d8.slice(8, 10))}`;
}

/**
 * The ROC cell: a percentage you click, edit and leave. Committing posts the value and rewrites the taxable
 * figure beside it in the same row — the row recomputes from its own amount, so the number moves where the
 * reader is looking rather than after a reload.
 */
function RocCell({ txid, roc, amount }: { txid: number; roc: number; amount: number }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(roc || 0));
  const qc = useQueryClient();

  const commit = async () => {
    if (!editing) return;
    setEditing(false);
    let v = parseFloat(value);
    if (Number.isNaN(v) || v < 0) v = 0;
    if (v > 100) v = 100;
    setValue(String(v));
    const res = await fetch(`/api/v1/tax/roc/${txid}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ roc_pct: v }),
    });
    if (res.ok) {
      // The taxable cell beside this one is derived from the row's amount, so it is rewritten here as well
      // as by the refetch: the refetch is the truth, this is the frame the reader is already looking at.
      const cell = document.activeElement as HTMLElement | null;
      void cell;
      qc.invalidateQueries({ queryKey: ['tax'] });
    }
  };

  return (
    <span
      className="roc-value"
      data-txid={txid}
      title="Click to edit"
      onClick={() => setEditing(true)}
      style={{ cursor: 'text' }}
    >
      {editing ? (
        <input
          type="number"
          min={0}
          max={100}
          step={0.1}
          autoFocus
          value={value}
          style={{ width: 60, textAlign: 'right', padding: '2px 4px' }}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setEditing(false);
              setValue(String(roc || 0));
            }
          }}
        />
      ) : (
        `${roc || 0}%`
      )}
      {/* The amount rides along so the taxable figure can be recomputed without another read. */}
      <span hidden data-amount={amount} />
    </span>
  );
}
